export const ASK_PUNTT_CAMPAIGN_PATH = "/ask-puntt/projects/$projectId";
export const ASK_PUNTT_CHAT_PATH = "/ask-puntt/$chatId";

export type AskPunttChatNavSource = {
  _id: string;
  projectId?: string | null;
  projectChatKind?: "shared" | "personal" | null;
};

export function isProjectPersonalChat(chat: AskPunttChatNavSource): boolean {
  return Boolean(chat.projectId && chat.projectChatKind === "personal");
}

export function askPunttChatNavLink(chat: AskPunttChatNavSource) {
  if (isProjectPersonalChat(chat)) {
    return {
      to: ASK_PUNTT_CAMPAIGN_PATH,
      params: { projectId: chat.projectId! },
      search: { chatId: chat._id },
    } as const;
  }
  return {
    to: ASK_PUNTT_CHAT_PATH,
    params: { chatId: chat._id },
  } as const;
}

/**
 * A router match as consumed by the sidebar active-state checks. We read the
 * route PARAMS (`projectId` / `chatId`) rather than the `pathname` string,
 * because the deployed epic env serves carrie under a basepath (e.g.
 * `/ap-sidebar-hierarchy`) — so `match.pathname` is NOT the bare
 * `/ask-puntt/projects/<id>` and a hardcoded string compare silently never
 * matches. Params are basepath- and trailing-slash-immune.
 */
export type AskPunttRouteMatch = {
  pathname?: string;
  params?: Record<string, unknown>;
};

export function isAskPunttChatNavActive(
  chat: AskPunttChatNavSource,
  matches: AskPunttRouteMatch[],
  locationSearch?: Record<string, unknown>,
): boolean {
  if (isProjectPersonalChat(chat)) {
    const onProject = matches.some(
      (m) => m.params?.projectId === chat.projectId,
    );
    if (!onProject) return false;
    const searchChatId =
      typeof locationSearch?.chatId === "string"
        ? locationSearch.chatId
        : undefined;
    return searchChatId === chat._id;
  }
  return matches.some((m) => m.params?.chatId === chat._id);
}

export function isViewingAskPunttChat(
  chat: AskPunttChatNavSource,
  pathname: string,
  searchParams: URLSearchParams,
): boolean {
  if (isProjectPersonalChat(chat)) {
    // `pathname` here comes from `window.location.pathname`, which DOES carry
    // the deployed basepath — so match the route segment as a suffix rather
    // than an exact string.
    return (
      pathname.endsWith(`/ask-puntt/projects/${chat.projectId}`) &&
      searchParams.get("chatId") === chat._id
    );
  }
  return pathname.includes(`/ask-puntt/${chat._id}`);
}
